import React, {useEffect, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useParams} from "react-router";
import materialService from "../../../../../services/web/material-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import materialGroupService from "../../../../../services/web/material-group-service.js";
import Alert from "../../../../../components/alert/alert.jsx";
import {FaPlus} from "react-icons/fa";
import InputSwitch from "../../../../../components/input/input-switch.jsx";

const initialData = {
    id: "",
    description: "",
    group_id: "",
    is_gun: false,
    is_unique: false,
    is_caution: false,
    decrease_term: false,
    terms_use: "",
    active: true
}

const MaterialForm = () => {
    const navigate = navigateHook();
    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState(initialData)
    const [materialSelected, setMaterialSelected] = useState("")

    const onChange = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}))
    const onChangeSelect = (id, value) => setData(prev => ({...prev, [id]: value}))
    const handleCheckBox = ({target: {id, checked}}) => {
        setData(prev => {
            const ret = {...prev, [id]: checked}
            if (id === "is_gun") {
                ret.is_unique = checked
                ret.is_caution = checked
            }

            if (id === "is_unique") {
                ret.is_caution = checked
            }

            return (ret)
        })
    }

    useEffect(() => {
        if (materialSelected) {
            setIsLoading(true)
            materialService.getById(materialSelected)
                .then((data) => {
                    setData(prevState => ({...prevState, terms_use: (data.terms_use || "")}))
                })
                .finally(() => setIsLoading(false))
        }
    }, [materialSelected]);

    const { data: material, refetch, status } = useQuery({
        queryKey: ['material', {id}],
        queryFn: async () => await materialService.getById(id),
        enabled: !!id
    });

    useEffect(() => {
        if (status === "success") {
            const materialNormalized = projectUtils.normalizeData(material)
            setData(materialNormalized)
        }
    }, [material, status]);

    const {data: hasMovement} = useQuery({
        queryKey: ['material-has-movements', {id}],
        queryFn: async () => {
            const {hasMovement} = await materialService.hasMovements(id)
            return hasMovement
        },
        enabled: !!id
    });

    const { data: groups } = useQuery({
        queryKey: ['materials-groups'],
        queryFn: async () => await materialGroupService.options(),
    });

    const {data: materials, isLoading: isLoadingMaterials} = useQuery({
        queryKey: ['materials'],
        queryFn: () => materialService.optionsAll(),
    });

    const saveAdd = () => save(true)

    const save = (saveAdd) => {
        setIsLoading(true);

        const formData = new FormData()
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        materialService.save(formData, id)
            .then(({data}) => {
                projectUtils.success(!id);

                if (saveAdd) {
                    setData(initialData)
                    setMaterialSelected("")
                    navigate("/materials/form");
                    return;
                }

                if (id) {
                    refetch()
                    return;
                }

                navigate(`/materials/form/${data.id}`);

            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    const remove = async () => {
        if (hasMovement) {
            projectUtils.swalErrorMessage('', 'Este material possui movimentações e não pode ser excluído.', 'Ok');
            return
        }

        projectUtils.swalMessage(
            'warning',
            'Excluir',
            `Excluir o material "${data.description}"?`,
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if (!isConfirmed) return
                materialService.delete(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso")
                        navigate("/materials")
                    })
                    .catch(error => {
                        console.error(error);
                        let message = 'Não foi possível excluir.';
                        if (error.response.status !== 500) {
                            message = error.response.data.message;
                        }

                        projectUtils.swalErrorMessage('', message, 'Ok');
                    });
            }
        );
    }

    const openForm = () => {
        const urlDev = `/materials/form`;
        navigate(urlDev)
    }

    return (
        <Template isLoading={isLoading || isLoadingMaterials}>
            <Template.Header>
                <Template.Title>
                    Material
                </Template.Title>
                {
                    id &&
                    <Button target={"primary-outline"} onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar material</span>
                    </Button>
                }
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4 mb-4">
                            <div className="col-span-12">
                                <InputSwitch
                                    id="active"
                                    label="Ativo"
                                    checked={data.active}
                                    onChange={(value) => setData(prevState => ({...prevState, active: value}))}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-8">
                                <InputForm
                                    disabled={hasMovement}
                                    id="description"
                                    name={"description"}
                                    value={data.description}
                                    label="Descrição *"
                                    onChange={onChange}
                                />
                            </div>
                            <div className="col-span-4">
                                <SelectComponent
                                    isDisabled={hasMovement}
                                    name="group_id"
                                    value={data.group_id}
                                    options={groups || []}
                                    label={"Grupo *"}
                                    onChange={(selected) => onChangeSelect("group_id", selected?.value)}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-2 mt-3">
                            <div className="col-span-12">
                                <InputCheckbox disabled={hasMovement} id="is_gun" checked={data.is_gun} label="É arma de fogo?" onChange={handleCheckBox}/>
                            </div>

                            <div className="col-span-12">
                                <InputCheckbox
                                    disabled={data.is_gun || hasMovement}
                                    id="is_unique"
                                    checked={data.is_unique}
                                    label="Cada unidade é controlada por número de série ou patrimônio?"
                                    onChange={handleCheckBox}/>
                            </div>

                            <div className="col-span-12">
                                <InputCheckbox
                                    disabled={data.is_gun || data.is_unique || hasMovement}
                                    id="is_caution"
                                    checked={data.is_caution}
                                    label="Pode ser usado em cautela?"
                                    onChange={handleCheckBox}/>
                            </div>

                            <div className="col-span-12">
                                <InputCheckbox disabled={hasMovement} id="decrease_term" checked={data.decrease_term} label="Exigir termo de baixa do material?" onChange={handleCheckBox}/>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Header className={"gap-2"}>
                        <Card.Title>Termo de uso</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-4">
                                <SelectComponent
                                    id="material"
                                    name="material"
                                    value={materialSelected}
                                    options={materials || []}
                                    label={"Selecionar termo de uso de outro material"}
                                    onChange={(selected) => setMaterialSelected(selected?.value)}
                                />
                            </div>
                            <div className="col-span-12">
                                <InputTextarea
                                    textAreaClass={"min-h-[200px]"}
                                    value={data.terms_use}
                                    id="terms_use"
                                    label="Termo de uso"
                                    onChange={onChange}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        <div className="flex justify-end gap-2 w-full items-center">
                            {hasMovement && <Alert className={"p-[5px_10px]!"} target="warning">Os campos não podem ser editados devido à movimentação do material.</Alert>}
                            {!id && <Button target={"primary-outline"} onClick={saveAdd} actionFeedbackType='save'>Salvar e adicionar novo</Button>}
                            {id && <Button disabled={hasMovement} target={"danger"} onClick={remove} actionFeedbackType='delete'>Excluir</Button>}
                            <Button onClick={() => save(false)} actionFeedbackType='save'>Salvar</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default MaterialForm;